import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, TrendingDown, Calendar, DollarSign, Users, Trophy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface HistoricalPerformanceComparisonProps {
  entityId: string;
  entityType: 'team' | 'venue' | 'coach';
}

interface SeasonData {
  season_name: string;
  season_year: number;
  games_played?: number;
  total_participants?: number;
  total_revenue?: number;
  avg_attendance?: number;
}

export const HistoricalPerformanceComparison: React.FC<HistoricalPerformanceComparisonProps> = ({
  entityId,
  entityType
}) => {
  const [seasonData, setSeasonData] = useState<SeasonData[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadHistoricalData();
  }, [entityId, entityType]);

  const loadHistoricalData = async () => {
    try {
      const { data, error } = await supabase
        .from('archived_seasons')
        .select('*')
        .eq('entity_id', entityId)
        .eq('entity_type', entityType)
        .order('season_year', { ascending: true });

      if (error) throw error;

      setSeasonData(data || []);
    } catch (error) {
      console.error('Error loading historical data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load historical performance data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTrend = (data: SeasonData[], key: keyof SeasonData) => {
    if (data.length < 2) return 0;
    const latest = data[data.length - 1][key] as number;
    const previous = data[data.length - 2][key] as number;
    return previous ? ((latest - previous) / previous) * 100 : 0;
  };

  const revenueData = seasonData.map(s => ({
    season: s.season_name,
    revenue: s.total_revenue || 0
  }));

  const participantsData = seasonData.map(s => ({
    season: s.season_name,
    participants: s.total_participants || 0,
    games: s.games_played || 0
  }));

  const revenueTrend = calculateTrend(seasonData, 'total_revenue');
  const participantsTrend = calculateTrend(seasonData, 'total_participants');
  const gamesTrend = calculateTrend(seasonData, 'games_played');

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading historical data...</div>
        </CardContent>
      </Card>
    );
  }

  if (seasonData.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">No historical data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Historical Performance Comparison
        </CardTitle>
        <CardDescription>Season-over-season performance metrics</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Trends */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue Trend</p>
                  <p className="text-2xl font-bold text-foreground">
                    {revenueTrend > 0 ? '+' : ''}{revenueTrend.toFixed(1)}%
                  </p>
                </div>
                {revenueTrend >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Participants Trend</p>
                  <p className="text-2xl font-bold text-foreground">
                    {participantsTrend > 0 ? '+' : ''}{participantsTrend.toFixed(1)}%
                  </p>
                </div>
                {participantsTrend >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-muted/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Games Trend</p>
                  <p className="text-2xl font-bold text-foreground">
                    {gamesTrend > 0 ? '+' : ''}{gamesTrend.toFixed(1)}%
                  </p>
                </div>
                {gamesTrend >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-green-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="revenue" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="engagement">Engagement</TabsTrigger>
          </TabsList>

          <TabsContent value="revenue" className="mt-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="season" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  name="Revenue ($)"
                />
              </LineChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="engagement" className="mt-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={participantsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="season" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="participants" fill="hsl(var(--primary))" name="Participants" />
                <Bar dataKey="games" fill="hsl(var(--secondary))" name="Games" />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
